import { Box, Typography, useTheme, keyframes } from '@mui/material';
import { isLiteTier, isStandardTier } from '@utils/tiers';
import { useState, useEffect } from 'react';
import { OptimisticMove } from '../context/OptimisticMoveContext';

interface ScheduleOptimisticBlockProps {
  optimisticMove: OptimisticMove;
  leftPosition: number;
  zoom: number;
  isRealMovePresent?: boolean;
}

// Pulse animation for loading states
const pulse = keyframes`
  0%, 100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
`;

const ScheduleOptimisticBlock = ({
  optimisticMove,
  leftPosition,
  zoom,
  isRealMovePresent = false,
}: ScheduleOptimisticBlockProps) => {
  const theme = useTheme();
  const { status, tier, laneDurationSec, inverseLaneDurationSec, driverName } = optimisticMove;
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [shouldRender, setShouldRender] = useState(true);

  // Handle fade-out when real move appears
  useEffect(() => {
    if (isRealMovePresent && !isFadingOut) {
      setIsFadingOut(true);
      const timeout = setTimeout(() => {
        setShouldRender(false);
      }, 500); // Match fade duration
      return () => clearTimeout(timeout);
    }
  }, [isRealMovePresent, isFadingOut]);

  // Don't render if we've completed the fade-out
  if (!shouldRender) {
    return null;
  }

  // Calculate durations
  // For concierge-pu: driver travels TO customer (inverse), appointment happens, then travels BACK (forward)
  // inverseLaneDurationSec = travel TO customer (before appointment)
  // laneDurationSec = travel BACK to dealer (after appointment)
  const travelToCustomerMin = inverseLaneDurationSec ? inverseLaneDurationSec / 60 : (laneDurationSec ? laneDurationSec / 60 : 60);
  const travelBackMin = laneDurationSec ? laneDurationSec / 60 : 60;
  const totalDurationMin = travelToCustomerMin + travelBackMin;

  // Total width is travel to + travel back
  const width = totalDurationMin * zoom;

  // Offset position to the left by the travel-to-customer duration
  // Because leftPosition is based on appointment time, but block starts earlier
  const adjustedLeftPosition = leftPosition - (travelToCustomerMin * zoom);

  // Status-based configuration
  const getStatusConfig = () => {
    switch (status) {
      case 'creating':
        return {
          label: 'Creating...',
          borderColor: theme.palette.info.main,
          bgColor: theme.palette.info.light,
          textColor: theme.palette.info.dark,
          animate: true,
        };
      case 'offering':
        return {
          label: isStandardTier(tier)
            ? 'Creating...'
            : driverName
              ? `Offering to ${driverName}...`
              : 'Offering to driver...',
          borderColor: isStandardTier(tier) ? theme.palette.info.main : theme.palette.warning.main,
          bgColor: isStandardTier(tier) ? theme.palette.info.light : theme.palette.warning.light,
          textColor: isStandardTier(tier) ? theme.palette.info.dark : theme.palette.warning.dark,
          animate: true,
        };
      case 'completed':
        return {
          label: isStandardTier(tier)
            ? 'Created. Fetching move details...'
            : driverName
              ? `Offered to ${driverName}. Fetching move details...`
              : 'Offered. Fetching move details...',
          borderColor: theme.palette.success.main,
          bgColor: theme.palette.success.light,
          textColor: theme.palette.success.dark,
          animate: true,
        };
      case 'failed':
        return {
          label: 'Failed',
          borderColor: theme.palette.error.main,
          bgColor: theme.palette.error.light,
          textColor: theme.palette.error.dark,
          animate: false,
        };
      default:
        return {
          label: 'Loading...',
          borderColor: theme.palette.grey[400],
          bgColor: theme.palette.grey[200],
          textColor: theme.palette.grey[600],
          animate: true,
        };
    }
  };

  const config = getStatusConfig();

  return (
    <Box
      sx={{
        position: 'absolute',
        left: `${adjustedLeftPosition}px`,
        top: 8,
        width: `${width}px`,
        height: '64px',
        borderRadius: '8px',
        border: `2px dashed ${config.borderColor}`,
        bgcolor: config.bgColor,
        opacity: isFadingOut ? 0 : 0.9,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        pointerEvents: 'none',
        // Smooth fade-out transition when real move appears
        transition: isFadingOut ? 'opacity 0.5s ease-out' : undefined,
        // Pulse animation for loading states (only when not fading)
        ...(config.animate && !isFadingOut && {
          animation: `${pulse} 1.5s ease-in-out infinite`,
        }),
      }}
    >
      {/* Status label */}
      <Typography
        sx={{
          fontSize: '11px',
          fontWeight: 600,
          color: config.textColor,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          userSelect: 'none',
          textAlign: 'center',
          px: 1,
          // Truncate long driver names
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {config.label}
      </Typography>

      {/* Tier indicator for lite during offering */}
      {isLiteTier(tier) && status === 'offering' && (
        <Typography
          sx={{
            mt: 0.5,
            fontSize: '9px',
            color: config.textColor,
            opacity: 0.8,
            userSelect: 'none',
          }}
        >
          Waiting for response
        </Typography>
      )}

      {/* Duration indicator */}
      <Typography
        sx={{
          mt: 0.5,
          fontSize: '12px',
          fontWeight: 700,
          color: config.textColor,
          userSelect: 'none',
          lineHeight: 1,
        }}
      >
        {Math.round(totalDurationMin)} min
      </Typography>
    </Box>
  );
};

export default ScheduleOptimisticBlock;
